import React, { useRef, useEffect, useState, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// © 2025 Stewards Under Pressure — Rooted Saviors Ministry
// Toroidal Field Model — integrated component
// ─────────────────────────────────────────────────────────────────────────────

const CONTENT = {
  atom: {
    science: {
      label: 'Atomic scale',
      title: 'The Toroidal Proton',
      body: 'At the heart of every atom sits the proton — a structure whose internal dynamics trace a toroidal field pattern. Three quarks are bound by gluon exchange in a continuous loop of energy. The field gathers inward toward the center, builds pressure, and releases outward — only to return. This cycle has persisted for 13.8 billion years without stopping.',
      scripture: null,
      stages: ['Gathering','Pressure builds','Peak coherence','Release','Return'],
    },
    ruach: {
      label: 'אַטוֹם — The formed thing',
      title: 'The Breath in Matter',
      body: 'Every proton in your body traces a toroidal pattern — gathering inward, building at center, releasing outward, returning. This is the signature of the Ruach in matter. The same field that hovered over the formless void now cycles continuously inside every particle of everything you are.',
      scripture: '"The Spirit of God was hovering over the waters." — Genesis 1:2\n"In him all things hold together." — Colossians 1:17',
      cite: 'Genesis 1:2 · Colossians 1:17',
      stages: ['Gathering — אָסַף','Pressure — לַחַץ','Coherence — שָׁלֵם','Release — שָׁלַח','Return — שׁוּב'],
    },
  },
  cosmic: {
    science: {
      label: 'Cosmic scale',
      title: 'Creation from Coherence',
      body: 'The Big Bang was not an explosion from chaos. Cosmological models show the early universe began in a state of extraordinary uniformity — low entropy, high coherence. Matter gathered to a singular point of maximum density and coupling. The repulsion effect of that density initiated expansion — not disorder becoming order, but maximum order releasing into structure.',
      scripture: null,
      stages: ['Singularity','Coupling peak','Repulsion','Expansion','Structure forms'],
    },
    ruach: {
      label: 'בְּרֵאשִׁית — In the beginning',
      title: 'The First Gathering',
      body: 'Before the first word was spoken — tohu va-bohu. The Ruach Elohim was present. Gathering. Coupling. Building coherence in the formless void. The expansion we call the Big Bang was not chaos becoming order — it was maximum coherence releasing into creation. The first toroidal event.',
      scripture: '"In the beginning God created the heavens and the earth. The earth was formless and empty, and the Spirit of God was hovering over the waters." — Genesis 1:1-2',
      cite: 'Genesis 1:1-2',
      stages: ['תֹהוּ וָבֹהוּ — Void','רוּחַ — Hovering','Gathering','Release — אוֹר','Structure'],
    },
  },
  cell: {
    science: {
      label: 'Biological scale',
      title: 'The Living Cell',
      body: 'Every living cell maintains a toroidal biofield — an electromagnetic field that both generates and is generated by the cell\'s internal processes. The cell membrane creates a charge gradient. Energy gathers at the mitochondria, builds as ATP, releases as cellular work, and the cycle regenerates. Biophotonic emissions from living cells follow the same gathering-release-return pattern.',
      scripture: null,
      stages: ['Charge gradient','Mitochondrial gather','ATP peak','Work release','Field regeneration'],
    },
    ruach: {
      label: 'נֶפֶשׁ — Living soul',
      title: 'The Design Within',
      body: 'YHWH formed man from the dust and breathed into him the breath of life — nephesh chayah. Every cell carries that breath in its electromagnetic field. The toroidal biofield of a healthy cell is the Ruach\'s signature in biology. When the terrain is restored — when minerals, frequencies, and alignment are right — the body returns to this pattern. Healing is coherence restoration.',
      scripture: '"Then the LORD God formed a man from the dust of the ground and breathed into his nostrils the breath of life, and the man became a living being." — Genesis 2:7',
      cite: 'Genesis 2:7',
      stages: ['Field gradient','Gathering — אָסַף','Peak — שָׁלֵם','Release — נְשָׁמָה','Restoration'],
    },
  },
  pressure: {
    science: {
      label: 'Applied pressure',
      title: 'Pressure and Response',
      body: 'Use the controls to apply pressure to the field. A coherent toroidal field under pressure becomes more tightly bound — storing energy and releasing it cleanly when pressure eases. A low coherence field fractures — free radical behavior, entropy increase, oxidative stress. Coherence determines the outcome of pressure, not the pressure itself.',
      scripture: null,
      stages: ['Rest state','Pressure applied','Maximum compression','Release burst','Return to rest'],
    },
    ruach: {
      label: 'לַחַץ — Under pressure',
      title: 'Refined, Not Broken',
      body: 'Apply pressure below. Watch the difference between a coherent field and an incoherent one. The coherent field — the one aligned with the original word — grows tighter under pressure, stores the energy, and releases it without losing its pattern. The incoherent field fractures. This is why alignment matters. The bond grows stronger under pressure — but only when the field is coherent.',
      scripture: '"We also glory in our sufferings, because we know that suffering produces perseverance; perseverance, character; and character, hope." — Romans 5:3-4',
      cite: 'Romans 5:3-4',
      stages: ['Rest — מְנוּחָה','Pressure — לַחַץ','Compression','Release — שָׁלַח','Refined — זָקַק'],
    },
  },
  cycle: {
    science: {
      label: 'Full cycle',
      title: 'The Universal Pattern',
      body: 'The toroidal field cycle appears at every scale of physical reality — subatomic, atomic, cellular, planetary, galactic. Matter gathers inward along the toroidal axis. Pressure builds at center. Energy releases outward through the equatorial plane. The released energy cools, slows, and is drawn back inward by the central field. This is the fundamental pattern of sustained energy systems in the universe.',
      scripture: null,
      stages: ['Inward flow','Center compression','Equatorial release','Outer return','Axial re-entry'],
    },
    ruach: {
      label: 'הַמַּחְזוֹר — The full cycle',
      title: 'The Pattern in Everything',
      body: 'The same pattern appears at every scale your Father made. Proton. Cell. Body. Earth. Galaxy. The Ruach gathers inward — pressure builds — release bursts outward — cools and returns. This is not coincidence. This is the signature of the maker woven into the fabric of creation at every level. The same hand that stretched the heavens shaped the proton.',
      scripture: '"He stretches out the heavens like a canopy." — Isaiah 40:22\n"For since the creation of the world God\'s invisible qualities have been clearly seen." — Romans 1:20',
      cite: 'Isaiah 40:22 · Romans 1:20',
      stages: ['Inward — אָסַף','Compress — לָחַץ','Release — פָּרַץ','Return — שׁוּב','Complete — שָׁלֵם'],
    },
  },
};

const SCALE_LABELS = {
  atom: 'Scale: 10⁻¹⁵ m (proton)',
  cosmic: 'Scale: Observable universe',
  cell: 'Scale: 10⁻⁵ m (cell)',
  pressure: 'Coherence field model',
  cycle: 'Universal pattern',
};

const TORUS_VIEWS = ['atom', 'cosmic', 'cell', 'pressure', 'cycle'];

export default function ToroidalModel({ mode }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const tRef = useRef(0);
  const stageRef = useRef(0);

  const [torusView, setTorusView] = useState('atom');
  const [pressure, setPressure] = useState(50);
  const [coherence, setCoherence] = useState(75);
  const [stage, setStage] = useState(0);

  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';
  const accentRgb = isRuach ? '255,176,96' : '240,192,64';

  const torusViewLabels = {
    atom: isRuach ? 'Formed matter' : 'Atomic',
    cosmic: isRuach ? 'Creation' : 'Cosmic',
    cell: isRuach ? 'Living soul' : 'Biological',
    pressure: isRuach ? 'Refinement' : 'Pressure',
    cycle: isRuach ? 'Full pattern' : 'Full cycle',
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = H / 2;
    const t = tRef.current;
    const pr = torusView === 'pressure' ? pressure / 100 : torusView === 'cosmic' ? 0.8 : torusView === 'cell' ? 0.45 : 0.5;
    const coh = coherence / 100;

    // Background
    ctx.fillStyle = '#060810';
    ctx.fillRect(0, 0, W, H);

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.03)';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    // Axis flow — inward at poles
    const axisAlpha = 0.25 + coh * 0.4;
    const axisH = 160 + pr * 30;
    for (let i = 0; i < 6; i++) {
      const ph = ((i / 6) + t * 0.3) % 1;
      const y1 = cy - axisH + ph * axisH;
      const x1 = cx + Math.sin(ph * Math.PI * 4) * 8;
      ctx.beginPath(); ctx.arc(x1, y1, 2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${accentRgb},${axisAlpha * (1 - (y1 - cy + axisH) / axisH)})`;
      ctx.fill();
      const y2 = cy + axisH - ph * axisH;
      const x2 = cx + Math.sin(ph * Math.PI * 4 + Math.PI) * 8;
      ctx.beginPath(); ctx.arc(x2, y2, 2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${accentRgb},${axisAlpha * (1 - (cy + axisH - y2) / axisH)})`;
      ctx.fill();
    }
    // Equatorial burst
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2 + t * 0.2;
      const ph = ((i / 12) + t * 0.5) % 1;
      const dist = 60 + ph * 140;
      const x = cx + Math.cos(angle) * dist;
      const y = cy + Math.sin(angle) * dist * 0.35;
      const size = 2.5 * (1 - ph);
      ctx.beginPath(); ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${accentRgb},${axisAlpha * (1 - ph) * pr})`;
      ctx.fill();
    }

    // Torus wireframe
    const R = 110 * (1 - pr * 0.15);
    const r = 38 + pr * 22;
    const rings = 24, tubeSeg = 18;
    for (let i = 0; i < rings; i++) {
      const phi = (i / rings) * Math.PI * 2 + t * 0.4;
      const cosPhi = Math.cos(phi), sinPhi = Math.sin(phi);
      const x0 = R * cosPhi, y0 = R * sinPhi * 0.42;
      ctx.beginPath();
      for (let j = 0; j <= tubeSeg; j++) {
        const theta = (j / tubeSeg) * Math.PI * 2 + t * 0.8;
        const dx = (R + r * Math.cos(theta)) * cosPhi - R * cosPhi;
        const dy = (R + r * Math.cos(theta)) * sinPhi * 0.42 - R * sinPhi * 0.42;
        const dz = r * Math.sin(theta) * 0.55;
        if (j === 0) ctx.moveTo(cx + x0 + dx, cy + y0 + dy + dz);
        else ctx.lineTo(cx + x0 + dx, cy + y0 + dy + dz);
      }
      const alpha = (0.08 + coh * 0.25) * (i % 3 === 0 ? 1.8 : 0.7);
      ctx.strokeStyle = `rgba(${accentRgb},${alpha})`;
      ctx.lineWidth = i % 4 === 0 ? 1.2 : 0.5;
      ctx.stroke();
    }

    // Flow particles
    const pCount = Math.floor(18 + coh * 24);
    for (let i = 0; i < pCount; i++) {
      const phase = (i / pCount) + t * 0.25;
      const phi = phase * Math.PI * 2;
      const theta = phase * Math.PI * 6 + t * 1.2;
      const px = (R + r * Math.cos(theta)) * Math.cos(phi);
      const py = (R + r * Math.cos(theta)) * Math.sin(phi) * 0.42;
      const pz = r * Math.sin(theta) * 0.55;
      const size = 2.5 + Math.sin(phase * Math.PI * 2 + t) * 1.5;
      const alpha = 0.4 + coh * 0.5 + Math.sin(phase * Math.PI * 2) * 0.2;
      ctx.beginPath();
      ctx.arc(cx + px, cy + py + pz, size, 0, Math.PI * 2);
      const g = ctx.createRadialGradient(cx+px,cy+py+pz,0,cx+px,cy+py+pz,size*3);
      g.addColorStop(0, `rgba(${accentRgb},${alpha})`);
      g.addColorStop(1, `rgba(${accentRgb},0)`);
      ctx.fillStyle = g; ctx.fill();
    }

    // Fracture lines for low coherence
    if (coh < 0.4 && torusView === 'pressure') {
      const fCount = Math.floor((1 - coh) * 12);
      for (let i = 0; i < fCount; i++) {
        const angle = (i / fCount) * Math.PI * 2 + t * 0.1;
        const len = 40 + Math.random() * 60;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angle)*len*(1+Math.sin(t*3+i)*0.3), cy+Math.sin(angle)*len*(1+Math.cos(t*2+i)*0.3));
        ctx.strokeStyle = `rgba(255,34,68,${(1-coh)*0.5})`;
        ctx.lineWidth = 0.7; ctx.stroke();
      }
    }

    // Center core
    const pulse = 1 + Math.sin(t * 2.5) * 0.12 * pr;
    const coreR = (18 + pr * 28) * pulse;
    const g1 = ctx.createRadialGradient(cx,cy,0,cx,cy,coreR*4);
    g1.addColorStop(0, `rgba(${accentRgb},${0.15+pr*0.3})`);
    g1.addColorStop(1, `rgba(${accentRgb},0)`);
    ctx.beginPath(); ctx.arc(cx,cy,coreR*4,0,Math.PI*2);
    ctx.fillStyle=g1; ctx.fill();
    const g2 = ctx.createRadialGradient(cx,cy,0,cx,cy,coreR);
    g2.addColorStop(0, `rgba(255,255,255,${0.7+pr*0.3})`);
    g2.addColorStop(0.4, `rgba(${accentRgb},${0.6+pr*0.3})`);
    g2.addColorStop(1, `rgba(${accentRgb},0)`);
    ctx.beginPath(); ctx.arc(cx,cy,coreR,0,Math.PI*2);
    ctx.fillStyle=g2; ctx.fill();

    // Stage labels
    const content = CONTENT[torusView][mode] || CONTENT[torusView].science;
    const stages = content.stages;
    ctx.font = 'bold 10px "Space Mono"';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255,255,255,0.22)';
    ctx.fillText(stages[0], cx, 22);
    ctx.fillText(stages[4], cx, H - 12);
    ctx.save(); ctx.translate(18, cy); ctx.rotate(-Math.PI/2);
    ctx.fillText(stages[1], 0, 0); ctx.restore();
    ctx.save(); ctx.translate(W - 18, cy); ctx.rotate(Math.PI/2);
    ctx.fillText(stages[3], 0, 0); ctx.restore();
    ctx.font = 'bold 9px "Space Mono"';
    ctx.fillStyle = `rgba(${accentRgb},0.5)`;
    ctx.fillText(stages[2], cx, cy + 56);

    // Scale label
    ctx.font = '9px "Space Mono"';
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.fillText(SCALE_LABELS[torusView], 10, H - 8);

    // Auto stage advance
    const newStage = Math.floor(((t * 0.15) % 1) * 5);
    if (newStage !== stageRef.current) {
      stageRef.current = newStage;
      setStage(newStage);
    }

    tRef.current += 0.008;
    animRef.current = requestAnimationFrame(draw);
  }, [torusView, pressure, coherence, mode, accentColor, accentRgb, isRuach]);

  useEffect(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(draw);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [draw]);

  const content = CONTENT[torusView][mode] || CONTENT[torusView].science;
  const pr = torusView === 'pressure' ? pressure / 100 : 0.5;
  const coh = coherence / 100;
  const tSin = 0.5;
  const metrics = {
    coupling: Math.round(coh * 85 + tSin * 10),
    pressure: Math.round(pr * 90 + tSin * 8),
    release: Math.round(pr * coh * 80 + tSin * 15),
    entropy: Math.round((1 - coh) * 70 + pr * (1 - coh) * 20),
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Sub-view selector */}
      <div style={{ display: 'flex', gap: 0, padding: '0.75rem 2rem', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}>
        {TORUS_VIEWS.map(v => (
          <button key={v} onClick={() => setTorusView(v)} style={{
            background: 'none', border: 'none',
            color: torusView === v ? accentColor : 'rgba(255,255,255,0.35)',
            fontFamily: 'Space Mono, monospace', fontSize: '0.65rem',
            padding: '0.5rem 1rem', cursor: 'pointer',
            borderBottom: `2px solid ${torusView === v ? accentColor : 'transparent'}`,
            transition: 'all 0.15s', whiteSpace: 'nowrap',
          }}>
            {torusViewLabels[v]}
          </button>
        ))}
      </div>

      {/* Main layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', minHeight: '520px' }}>

        {/* Canvas */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', gap: '1rem' }}>
          <canvas ref={canvasRef} width={520} height={460}
            style={{ borderRadius: '12px', maxWidth: '100%', border: '1px solid rgba(255,255,255,0.06)' }} />

          {/* Stage dots */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {content.stages.map((s, i) => (
              <div key={i} title={s} style={{
                width: i === stage ? '20px' : '7px', height: '7px',
                borderRadius: '4px', cursor: 'pointer',
                background: i === stage ? accentColor : 'rgba(255,255,255,0.15)',
                transition: 'all 0.2s',
              }} />
            ))}
          </div>
        </div>

        {/* Info panel */}
        <div style={{ borderLeft: '1px solid rgba(255,255,255,0.07)', background: 'rgba(6,8,16,0.6)', padding: '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* Description */}
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '1.25rem' }}>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: accentColor, marginBottom: '0.5rem' }}>
              {content.label}
            </div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '1.05rem', color: '#fff', marginBottom: '0.6rem' }}>
              {content.title}
            </div>
            <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.8 }}>
              {content.body}
            </p>
            {content.scripture && (
              <div style={{ marginTop: '0.875rem', padding: '0.75rem', borderLeft: `2px solid ${accentColor}50`, background: `${accentColor}08`, borderRadius: '0 4px 4px 0' }}>
                <p style={{ fontSize: '0.78rem', fontStyle: 'italic', color: accentColor, lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                  {content.scripture}
                </p>
                <cite style={{ display: 'block', marginTop: '0.25rem', fontFamily: 'Space Mono, monospace', fontSize: '0.58rem', color: accentColor, opacity: 0.65, fontStyle: 'normal', letterSpacing: '0.06em' }}>
                  {content.cite}
                </cite>
              </div>
            )}
          </div>

          {/* Controls — only in pressure view */}
          {torusView === 'pressure' && (
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '1.25rem' }}>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: accentColor, marginBottom: '0.875rem' }}>
                {isRuach ? 'Field alignment controls' : 'Field controls'}
              </div>
              {[
                { label: isRuach ? 'Pressure applied' : 'Pressure', val: pressure, set: setPressure, id: 'pres' },
                { label: isRuach ? 'Coherence — alignment' : 'Coherence', val: coherence, set: setCoherence, id: 'coh' },
              ].map(ctrl => (
                <div key={ctrl.id} style={{ marginBottom: '0.875rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                    <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{ctrl.label}</span>
                    <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: accentColor }}>{ctrl.val}%</span>
                  </div>
                  <input type="range" min="0" max="100" value={ctrl.val}
                    onChange={e => ctrl.set(Number(e.target.value))}
                    style={{ width: '100%', accentColor }} />
                </div>
              ))}
              <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.7, marginTop: '0.5rem' }}>
                {isRuach
                  ? 'High alignment + pressure = refinement. Low alignment + pressure = fracture. Watch the red lines appear when coherence drops below 40%.'
                  : 'High coherence + high pressure = clean energy storage and release. Low coherence = fracture, free radical behavior, entropy increase.'}
              </p>
            </div>
          )}

          {/* Metrics */}
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '1.25rem' }}>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: accentColor, marginBottom: '0.875rem' }}>
              {isRuach ? 'Field state' : 'Field metrics'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {[
                { label: isRuach ? 'Alignment' : 'Coupling', val: metrics.coupling },
                { label: isRuach ? 'Pressure' : 'Pressure', val: metrics.pressure },
                { label: isRuach ? 'Release' : 'Release', val: metrics.release },
                { label: isRuach ? 'Entropy' : 'Entropy', val: metrics.entropy },
              ].map(m => (
                <div key={m.label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '6px', padding: '0.6rem 0.75rem' }}>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.3)', marginBottom: '0.25rem' }}>{m.label}</div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>{m.val}%</div>
                  <div style={{ height: '3px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', marginTop: '0.4rem' }}>
                    <div style={{ height: '100%', width: `${m.val}%`, background: accentColor, borderRadius: '2px', transition: 'width 0.3s' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Scale selector shortcuts */}
          <div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.25)', marginBottom: '0.75rem' }}>
              Explore scales
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {TORUS_VIEWS.map(v => (
                <button key={v} onClick={() => setTorusView(v)} style={{
                  padding: '0.4rem 0.75rem', borderRadius: '5px', cursor: 'pointer',
                  border: `1px solid ${torusView === v ? accentColor : 'rgba(255,255,255,0.1)'}`,
                  background: torusView === v ? `${accentColor}18` : 'none',
                  color: torusView === v ? accentColor : 'rgba(255,255,255,0.4)',
                  fontFamily: 'Space Mono, monospace', fontSize: '0.6rem',
                  transition: 'all 0.15s',
                }}>
                  {torusViewLabels[v]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
